import { useTranslation } from 'next-i18next'
import { useSnackbar } from 'notistack'
import { useCallback } from 'react'
import { useAuthProvider } from 'src/context/AuthProvider'
import useLogger from 'src/context/LoggerProvider/useLogger'
import {
  getDisplayMessageFromParsed,
  getRawErrorMessage,
  isExpectedErrorCode,
  parseRequestError
} from 'src/utils/errors'

type EnqueueErrorOptions = {
  /** Context line for the logger (e.g. 'Error saving checkout.'). */
  logMessage?: string
  /** Structured breadcrumb passed to the logger alongside the error. */
  logContext?: unknown
}

/**
 * Single entry point for surfacing a failed request to the user.
 *
 * Replaces the repeated `enqueueSnackbar(t('ERRORS.GENERIC')) + logger.error`
 * pattern in mutation `onError` callbacks. It:
 *  - logs the error once, at `warn` for expected outcomes (BAD_USER_INPUT /
 *    FORBIDDEN / UNAUTHENTICATED) and `error` for genuine system failures, so
 *    routine user-input errors don't trip the Datadog `logger.error` alerts;
 *  - maps the error to a user-facing message + variant via `getDisplayMessage`
 *    (real backend copy for BAD_USER_INPUT, friendly localized copy otherwise);
 *  - shows admins a `· [CODE] rawMessage` technical suffix on the
 *    friendly/generic cases (via `currentUser.isAdmin`).
 *
 * Callers with bespoke handling (KYC missing-fields field labels, AddPost
 * `errorStep` banner nav) keep their special branch and call `enqueueError`
 * only for the fallback path.
 */
export default function useErrorSnackbar() {
  const { t } = useTranslation('common')
  const { enqueueSnackbar } = useSnackbar()
  const logger = useLogger()
  const { currentUser } = useAuthProvider()
  const isAdmin = !!currentUser?.isAdmin

  return useCallback(
    (error: unknown, options?: EnqueueErrorOptions) => {
      const parsed = parseRequestError(error)

      // Call through `logger` rather than detaching the method. The Datadog
      // browser-logs SDK builds its level methods on `Logger.prototype` and
      // reads `this` inside (`isAuthorized(status, handlerType, this)` →
      // `logger.getHandler()`), so a bare `logger.warn` reference throws
      // "Cannot read properties of undefined (reading 'getHandler')" the
      // moment it's invoked — swallowing the real error it was logging.
      const level = isExpectedErrorCode(parsed.code) ? 'warn' : 'error'
      const logMessage = options?.logMessage ?? 'Request error'
      if (options?.logContext !== undefined) {
        logger[level](logMessage, options.logContext, error as Error)
      } else {
        logger[level](logMessage, error)
      }

      const { message, variant } = getDisplayMessageFromParsed(
        parsed,
        getRawErrorMessage(error),
        { t, isAdmin }
      )
      enqueueSnackbar(message, {
        variant,
        preventDuplicate: true,
        autoHideDuration: variant === 'warning' ? 8000 : 6000
      })
    },
    [t, enqueueSnackbar, logger, isAdmin]
  )
}
